import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { NgxExtendedPdfViewerService } from 'ngx-extended-pdf-viewer';
import { degrees, PDFDocument, rgb } from 'pdf-lib';
import { AuthService } from 'src/app/services/auth.service';
import { CourseService } from 'src/app/services/course.service';
import { ViewCourse, ViewCourseDefaultValues } from '../../course/course';
import { ebook, EbookDefaultValues } from '../../course/ebook';
import { MaterialsService } from 'src/app/services/materials.service';
import { faBookOpen, faFile, faFileArrowDown, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { ModalService } from 'src/app/services/modal.service';
import { TranslationService } from 'src/app/services/translation.service';
import { MaterialDownloadAuditLog } from '../../materials/materials';
import { Observable, of, Subscription } from 'rxjs';
import { map } from 'rxjs';

@Component({
    selector: 'app-pdf-viewer',
    templateUrl: './pdf-viewer.component.html',
    styleUrls: ['./pdf-viewer.component.scss'],
    standalone: false
})
export class PdfViewerComponent implements OnInit, OnDestroy {
  @Input() materialId: string = '';
  public hidePdfViewer = false;
  /** Material type label from API (e.g. "Video", "E-book"). */
  public materialType = '';
  /** Blob URL for in-browser video/audio playback. */
  public mediaUrl: string | null = null;
  /** Whether streamed blob is audio (else shown as video). */
  public streamIsAudio = false;
  public sidebarVisible = true;
  private _language: string = 'en-EN';// 'fr-FR';
  private loggedInUserPublicId: string = '';
  private participantName: string = '';
  private participantEmail: string = '';
  private currentDate: Date = moment().toDate();
  private courseId: string = '';
  private course: ViewCourse = ViewCourseDefaultValues();
  private materialName: string = '';
  public pdfSrc: string | undefined;
  private ebookModel: ebook = EbookDefaultValues();
  private pdfFormData: { [key: string]: any } = {};
  public formData: { [fieldName: string]: any } = {};
  public spreadMode: "off" | "even" | "odd" = "off";
  public faSinglePage: IconDefinition = faFile;
  public faDoublePage: IconDefinition = faBookOpen;
  public faDownload: IconDefinition = faFileArrowDown;
  modalSub!: Subscription;
  modalOpen = this.modalService.getModalState();
  modalContent = '';
  modalTitle = '';
  modalOkButtonValue = 'OK';
  modalCancelButtonValue = 'CANCEL';
  modalOkButtonText = 'Ok';
  modalCancelButtonText = 'Cancel';
  translations: Record<string, string> = {};

  constructor(private extendedPdfService: NgxExtendedPdfViewerService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private courseService: CourseService,
    private materialService: MaterialsService,
    private modalService: ModalService,
    private translationService: TranslationService) {
  }

  ngOnInit(): void {
    if (!this.authService.checkTokenExpiry()) {
      this.router.navigateByUrl('/bb-login');
    }

    var participantModel = this.authService.getUserLoginData();
    this.loggedInUserPublicId = participantModel.publicId;
    this.participantName = participantModel.agentName;
    this.participantEmail = participantModel.username;

    this.route.params.subscribe({
      next: (params) => {
        this.courseId = params['id'] ?? '';
        this.materialId = params['materialid'] ?? '';

        this.getCourseDetails(this.courseId);
      },
    });

    this.translationService.getTranslation().subscribe({
      next: (t) => { this.translations = t; }
    });

    this.afterConfirmModalOps();

    this.translationService.getTranslation().subscribe({
      next: (data) => {
        this.translations = data;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.modalSub) {
      this.modalSub.unsubscribe();
    }
    this.revokeMediaUrl();
  }

  private revokeMediaUrl(): void {
    if (this.mediaUrl && this.mediaUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.mediaUrl);
    }
    this.mediaUrl = null;
  }

  /** Video and Audio/Slides materials use HTML media elements instead of the PDF viewer. */
  public isStreamMediaMaterial(): boolean {
    const t = this.materialType;
    return t === 'Video' || t === 'Audio/Slides';
  }

  setSinglePageView(): void {
    this.spreadMode = "off";
  }

  setDoublePageView(): void {
    this.spreadMode = "even";
  }

  public reloadPDFFileAndData() {
    if (this.isStreamMediaMaterial()) {
      this.loadStreamMediaFile();
    } else {
      this.getSavedPdfData();
      this.loadPdfFile();
    }
  }

  private applyMaterialFromCourseList(materialId: string): void {
    if (!materialId) {
      this.materialName = '';
      this.materialType = '';
      this.pdfSrc = undefined;
      this.revokeMediaUrl();
      return;
    }

    const material = (this.course.materialList ?? []).find(
      (m: { id?: string; Id?: string }) => m.id === materialId || m.Id === materialId
    );

    if (!material) {
      this.materialName = '';
      this.materialType = '';
      this.pdfSrc = undefined;
      this.revokeMediaUrl();
      return;
    }

    this.materialName = material.materialName ?? material.MaterialName ?? '';
    this.materialType = material.type ?? material.Type ?? '';
    this.reloadPDFFileAndData();
  }

  public getCourseDetails(courseid: string) {
    this.courseService.getCourseDetailsForParticipant(courseid).subscribe({
      next:(response) => {
        console.log("get course details...");
        this.course = response;
        if (response.language == 'English') {
          this._language = 'en-EN';
        } else {
          this._language = 'fr-FR';
        }
        this.applyMaterialFromCourseList(this.materialId);
      }
    });
  }

  public loadPdfFile(): void {
    if (this.materialId) {
      this.revokeMediaUrl();
      this.pdfSrc = undefined;
      this.courseService.getCourseMaterialFile(this.courseId, this.materialId).subscribe({
        next: (data) => {
          if (data?.url) {
            this.pdfSrc = data.url;
          } else {
            console.error('PDF access URL missing:', data);
          }
        },
        error: (err) => console.error('Failed to load course material PDF', err),
      });
    }
  }

  /** Loads video/audio presigned URL for participant stream materials. */
  private loadStreamMediaFile(): void {
    if (!this.materialId || !this.courseId) {
      return;
    }
    this.revokeMediaUrl();
    this.courseService.getCourseMaterialFile(this.courseId, this.materialId).subscribe({
      next: (data) => {
        if (!data?.url) {
          console.error('Course material access URL missing:', data);
          return;
        }
        const mime = (data.contentType || '').toLowerCase();
        this.streamIsAudio = mime.startsWith('audio/');
        this.mediaUrl = data.url;
      },
      error: (err) => console.error('Failed to load course media', err),
    });
  }

  public getSavedPdfData() {
    this.pdfFormData = {};
    this.courseService.getCourseEbookData(this.courseId, this.materialId, this.loggedInUserPublicId).subscribe({
      next: (data) => {
        if (data?.formData && typeof data.formData === 'string' && data.formData.length > 0) {
          try {
            const parsed = JSON.parse(data.formData);
            this.pdfFormData = parsed && typeof parsed === 'object' ? parsed : {};
          } catch {
            this.pdfFormData = {};
          }
        }
        this.updateFormData();
      },
    });
  }

  public get language(): string {
    return this._language;
  }

  public set language(language: string) {
    this._language = language;
  }

  private initialized = false;

  public delayedUpdateFormData(): void {
    if (!this.extendedPdfService.isRenderQueueEmpty()) {
      setTimeout(() => this.delayedUpdateFormData(), 100);
    }

    for (let page = 1; page <= this.extendedPdfService.numberOfPages(); page++) {
      const pageIndex = page - 1;
      if (!this.extendedPdfService.hasPageBeenRendered(pageIndex)) {
          this.extendedPdfService.addPageToRenderQueue(pageIndex);
          break;
      }
    }

    setTimeout(() => {
      this.initialized = true;
      this.updateFormData();
    }, 100);
  }

  public updateFormData(): void {
    this.formData = {...this.formData, ...this.getFormData()};
  }

  public getFormData(): { [key: string]: any } {
    const data: { [fieldName: string]: any } = {};
    const fieldNames: { [key: string]: boolean } = this.getFieldNames();
    const saved = this.pdfFormData ?? {};

    for (const key in fieldNames) {
      const raw = saved[key];
      const isCheckboxOrRadio = fieldNames[key];
      if (isCheckboxOrRadio) {
        data[key] = typeof raw === 'boolean' ? raw : !!raw;
      } else {
        // Missing keys must not be `undefined` or ngx-extended-pdf-viewer shows the literal "undefined".
        if (raw === undefined || raw === null) {
          data[key] = '';
        } else if (typeof raw === 'string' && raw === 'undefined') {
          data[key] = '';
        } else {
          data[key] = raw;
        }
      }
    }

    return data;
  }

  public setFormData(data: { [key: string]: any }) {
    if (this.initialized) {
      let fieldNames: { [key: string]: boolean } = this.getFieldNames();

      for (const fieldName in fieldNames) {
        const v = data[fieldName];
        if (fieldNames[fieldName]) {
          this.pdfFormData[fieldName] = typeof v === 'boolean' ? v : !!v;
        } else {
          this.pdfFormData[fieldName] =
            v === undefined || v === null ? '' : v;
        }
      }
    }
  }

  public getFieldNames(): { [key: string]: boolean } {
    const fieldNames: { [key: string]: boolean } = {};
    const container = document.querySelector("#viewerContainer");

    const inputs = container?.querySelectorAll('input[type="text"]') ?? document.querySelectorAll('input[type="text"]');
    inputs.forEach(input => {
      const inputElement = input as HTMLInputElement;
      fieldNames[inputElement.name] = false;
    });

    const textareas = container?.querySelectorAll('textarea') ?? document.querySelectorAll('textarea');
    textareas.forEach(input => {
      const inputElement = input as HTMLTextAreaElement;
      fieldNames[inputElement.name] = false;
    });

    const checkboxes = container?.querySelectorAll('input[type="checkbox"]') ?? document.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(input => {
      const inputElement = input as HTMLInputElement;
      fieldNames[inputElement.name] = true;
    });

    const radios = container?.querySelectorAll('input[type="radio"]') ?? document.querySelectorAll('input[type="radio"]');
    radios.forEach(input => {
      const inputElement = input as HTMLInputElement;
      fieldNames[inputElement.name] = true;
    });

    const dropdowns = container?.querySelectorAll('select') ?? document.querySelectorAll('select');
    dropdowns.forEach(input => {
      const inputElement = input as HTMLSelectElement;
      fieldNames[inputElement.name] = false;
    });

    return fieldNames;
  }

  public onFormChange(): void {
    this.courseService.submitPdfFormData(this.buildEbookPayloadFromDom()).subscribe();
  }

  /**
   * Reads current field values from the viewer DOM and returns the API payload.
   */
  private buildEbookPayloadFromDom(): ebook {
    const formData: { [key: string]: any } = {};
    const container = document.querySelector('#viewerContainer');

    const inputs = container?.querySelectorAll('input[type="text"]') ?? document.querySelectorAll('input[type="text"]');
    inputs.forEach((input) => {
      const inputElement = input as HTMLInputElement;
      formData[inputElement.name] = inputElement.value;
    });

    const textareas = container?.querySelectorAll('textarea') ?? document.querySelectorAll('textarea');
    textareas.forEach((textarea) => {
      const textareaElement = textarea as HTMLTextAreaElement;
      formData[textareaElement.name] = textareaElement.value;
    });

    const checkboxes = container?.querySelectorAll('input[type="checkbox"]') ?? document.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach((checkbox) => {
      const checkboxElement = checkbox as HTMLInputElement;
      formData[checkboxElement.name] = checkboxElement.checked;
    });

    const radios = container?.querySelectorAll('input[type="radio"]') ?? document.querySelectorAll('input[type="radio"]');
    radios.forEach((radio) => {
      const radioElement = radio as HTMLInputElement;
      formData[radioElement.name] = radioElement.checked;
    });

    const dropdowns = container?.querySelectorAll('select') ?? document.querySelectorAll('select');
    dropdowns.forEach((dropdown) => {
      const dropdownElement = dropdown as HTMLSelectElement;
      formData[dropdownElement.name] = dropdownElement.value;
    });

    this.ebookModel.courseId = this.courseId;
    this.ebookModel.materialId = this.materialId;
    this.ebookModel.participantId = this.loggedInUserPublicId;
    this.ebookModel.formData = JSON.stringify(formData);
    return { ...this.ebookModel };
  }

  /**
   * Saves the current PDF form to the server immediately (used before switching materials).
   */
  public saveCurrentPdfFormNow(): Observable<void> {
    if (!this.materialId || !this.courseId) {
      return of(void 0);
    }
    if (this.isStreamMediaMaterial()) {
      return of(void 0);
    }

    const payload = this.buildEbookPayloadFromDom();
    return this.courseService.savePdfFormDataImmediate(payload).pipe(map(() => void 0));
  }

  public onError(error: any): void {
    console.error('PDF loading error:', error);
  }

  public confirmDownload() {
    const title = this.translations['materialDownloadNoticeTitle'] ?? 'Copyright Notice';
    const content = this.translations['materialDownloadNoticeContent'] ??
      'By accessing or downloading any of the Worksheets or Bounce Back Books or any other materials provided, I am granted a limited copyright license solely to download and save such materials for my personal use only; I hereby agree and acknowledge that such materials are copyrighted materials provided to me under license for my personal use only; and I shall not distribute, send, reproduce, or provided access to such materials (including the electronic copies) to any other person.';
    const downloadButtonText = this.translations['materialDownloadButtonText'] ?? 'Download';
    const cancelButtonText = this.translations['cancelButtonText'] ?? 'Cancel';

    this.askConfirmation(downloadButtonText, 'DOWNLOAD', cancelButtonText, 'CANCEL', content, title);
  }

  public auditDownload(id: string) {
    var model: MaterialDownloadAuditLog = {
      courseId: this.courseId,
      materialId: id
    };

    this.materialService.auditMaterialDownload(model).subscribe();
  }

  public download(id: string) {
    this.courseService.getCourseMaterialFile(this.courseId, id).subscribe({
      next: (fileResponse) => {
        if (!fileResponse?.url) {
          return;
        }
        const a = document.createElement('a');
        a.href = fileResponse.url;
        a.download = (fileResponse.fileName || this.materialName || 'download').replace(/[/\\?%*:|"<>]/g, '-');
        a.target = '_blank';
        a.click();
      }
    });
  }

  private askConfirmation(
    okButtonText: string,
    okButtonValue: string,
    cancelButtonText: string,
    cancelButtonValue: string,
    content: string,
    title: string) {
      this.modalContent = content;
      this.modalTitle = title;
      this.modalOkButtonValue = okButtonValue;
      this.modalCancelButtonValue = cancelButtonValue;
      this.modalOkButtonText = okButtonText;
      this.modalCancelButtonText = cancelButtonText;

      this.modalService.openModal();
  }

  private afterConfirmModalOps() {
    this.modalSub = this.modalService
      .getButtonClicked()
      .subscribe((button) => {
        switch (button) {
          case 'DOWNLOAD':
            this.auditDownload(this.materialId);
            if (this.isStreamMediaMaterial() && this.mediaUrl) {
              this.downloadStreamMedia();
            } else {
              this.downloadPdfWithWatermark();
            }
          break;
          default:
            this.modalService.closeModal();
        }
      });
  }

  private downloadStreamMedia(): void {
    if (!this.mediaUrl) {
      return;
    }
    const a = document.createElement('a');
    a.href = this.mediaUrl;
    a.download = (this.materialName || 'download').replace(/[/\\?%*:|"<>]/g, '-');
    a.click();
    this.modalService.closeModal();
  }

  public async downloadPdfWithWatermark() {
    try {
      const pdfBlob = await this.extendedPdfService.getCurrentDocumentAsBlob();
      if (pdfBlob) {
        const pdfWithWatermark = await this.addWatermark(pdfBlob);

        const url = window.URL.createObjectURL(pdfWithWatermark);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.materialName + '.pdf';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  }

  private async addWatermark(pdfBlob: Blob) {

    const pdfBytes = await pdfBlob.arrayBuffer();
    const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: false });

    const pages = pdfDoc.getPages();
    const watermarkText = "Downloaded by: " + this.participantName + " (" + this.participantEmail + ") on " + this.currentDate.toDateString();

    pages.forEach((page) => {
      const { width, height } = page.getSize();
      page.drawText(watermarkText, {
        x: width / 9.2,
        y: height - 775,
        size: 10,
        color: rgb(0, 0, 0),
        opacity: 0.2,
        rotate: degrees(0),
      });

    });

    pdfDoc.setModificationDate(this.currentDate);
    pdfDoc.setProducer(this.participantName + " (" + this.participantEmail + ")");
    pdfDoc.setSubject(watermarkText);

    const pdfBytesWithWatermark = await pdfDoc.save();
    return new Blob([new Uint8Array(pdfBytesWithWatermark)], { type: 'application/pdf' });
  }
}
